import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CoachingFormDialog, { CoachingFormMode } from "./CoachingFormDialog";
import CloseCoachingDialog from "./CloseCoachingDialog";
import {
  AddManualUserCoaching,
  CloseUserCoaching,
  GetAllCoaches,
  GetUserCoachings,
  UpdateUserCoaching,
} from "../../../api/AdminCoachPanel/coachApis";
import ResponseStatus from "../../../enums/ResponseStatus";
import ExamCategoryType from "../../../enums/ExamCategoryType";
import {
  CoachingState,
  CoachingStateLabel,
  formatDateTime,
  getCoachingState,
  toApiDateTime,
} from "./coachingDates";
import "./UserCoachingSection.css";

// Envelope checks: HTTP 200 → backend ResponseStatus → (caller reads ResponseData).
export const isSuccess = (res) =>
  !!res &&
  res.status === 200 &&
  !!res.data &&
  res.data.ResponseStatus === ResponseStatus.Success;

// Backend ResponseMessage (e.g. "Koçluk kaydı zaten kapalı.") when present —
// on a 200 with ResponseStatus 0 or on an axios error body — else the fallback.
export const responseMessage = (res, fallback) => {
  if (res && res.data && res.data.ResponseMessage && !isSuccess(res))
    return res.data.ResponseMessage;
  const errData = res && res.response && res.response.data;
  if (errData && (errData.ResponseMessage || errData.Error))
    return errData.ResponseMessage || errData.Error;
  return fallback;
};

const EXAM_CATEGORY_LABEL = Object.keys(ExamCategoryType).reduce((acc, key) => {
  acc[ExamCategoryType[key]] = key;
  return acc;
}, {});

function joinName(first, last) {
  const parts = [first, last].filter((x) => x && x !== "-");
  return parts.join(" ").trim() || "-";
}

// Option value is the coach PROFILE Id (what AddManualUserCoaching expects).
function mapCoachOption(c) {
  const name = joinName(c.Name, c.Surname);
  const cat = EXAM_CATEGORY_LABEL[c.ExamCategoryId];
  const d = c.CoachDetail;
  const seats =
    d && typeof d.TotalSeat === "number"
      ? ` — ${typeof d.StudentCount === "number" ? d.StudentCount : 0}/${d.TotalSeat}`
      : "";
  return {
    id: c.Id,
    label: `${name}${cat && cat !== "None" ? ` / ${cat}` : ""}${seats}`,
  };
}

// A user's coaching history with close / extend / manual-assign actions.
// Used on the Credits page (Administrators) and the standalone
// /CoachesAdminPanel/user-coachings page (CoachAdmins). The host page must
// render a react-toastify ToastContainer.
export default function UserCoachingSection({ userId, userLabel }) {
  const userAdminToken = sessionStorage.getItem("jwtP");
  const _user = JSON.parse(localStorage.getItem("user"));
  const [coachings, setCoachings] = useState([]);
  const [coachingsLoading, setCoachingsLoading] = useState(false);
  const [coachOptions, setCoachOptions] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(false);

  const [formMode, setFormMode] = useState(null); // CoachingFormMode | null
  const [formTarget, setFormTarget] = useState(null); // record for Update
  const [closeTarget, setCloseTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Guards against a slow response for a previous user overwriting the current one.
  const userReqRef = useRef(0);

  // Only a currently running coaching (open, started, not ended) blocks a new
  // manual assignment; expired or not-yet-started open records don't.
  const hasOpenCoaching = coachings.some(
    (c) => getCoachingState(c) === CoachingState.Active
  );

  /* ---- Data ------------------------------------------------------------- */
  const fetchCoachings = useCallback(
    async (forUserId, reqId) => {
      setCoachingsLoading(true);
      try {
        const res = await GetUserCoachings(userAdminToken, forUserId);
        if (reqId !== userReqRef.current) return;
        if (isSuccess(res) && Array.isArray(res.data.ResponseData)) {
          setCoachings(res.data.ResponseData);
        } else if (
          res &&
          res.status === 200 &&
          res.data &&
          res.data.ResponseStatus === ResponseStatus.DataIsNull
        ) {
          setCoachings([]);
        } else {
          setCoachings([]);
          toast.error(responseMessage(res, "Koçluk kayıtları alınamadı."));
        }
      } catch (err) {
        if (reqId !== userReqRef.current) return;
        console.error("GetUserCoachings error", err);
        setCoachings([]);
        toast.error("Bir hata oluştu, lütfen tekrar deneyiniz.");
      } finally {
        if (reqId === userReqRef.current) setCoachingsLoading(false);
      }
    },
    [userAdminToken]
  );

  const fetchCoaches = useCallback(
    async (forUserId, reqId) => {
      setCoachesLoading(true);
      try {
        const res = await GetAllCoaches(userAdminToken, null, forUserId);
        if (reqId !== userReqRef.current) return;
        if (isSuccess(res) && Array.isArray(res.data.ResponseData)) {
          setCoachOptions(
            res.data.ResponseData.filter((c) => c.Id != null)
              .map(mapCoachOption)
              .sort((a, b) =>
                a.label.localeCompare(b.label, "tr", { sensitivity: "base" })
              )
          );
        } else {
          setCoachOptions([]);
          toast.error(responseMessage(res, "Koç listesi alınamadı."));
        }
      } catch (err) {
        if (reqId !== userReqRef.current) return;
        console.error("GetAllCoaches (by user) error", err);
        setCoachOptions([]);
      } finally {
        if (reqId === userReqRef.current) setCoachesLoading(false);
      }
    },
    [userAdminToken]
  );

  useEffect(() => {
    const reqId = ++userReqRef.current;
    setCoachings([]);
    setCoachOptions([]);
    setFormMode(null);
    setFormTarget(null);
    setCloseTarget(null);
    if (!userId) {
      setCoachingsLoading(false);
      setCoachesLoading(false);
      return;
    }
    fetchCoachings(userId, reqId);
    fetchCoaches(userId, reqId);
  }, [userId, fetchCoachings, fetchCoaches]);

  const refreshCoachings = () => {
    if (userId) fetchCoachings(userId, userReqRef.current);
  };

  /* ---- Actions ---------------------------------------------------------- */
  const openAssign = () => {
    setFormTarget(null);
    setFormMode(CoachingFormMode.Assign);
  };

  const openUpdate = (coaching) => {
    setFormTarget(coaching);
    setFormMode(CoachingFormMode.Update);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormTarget(null);
  };

  const handleFormSubmit = async ({ CoachId, StartDate, WeekCount }) => {
    const isUpdate = formMode === CoachingFormMode.Update;
    setSubmitting(true);
    try {
      const res = isUpdate
        ? await UpdateUserCoaching(userAdminToken, {
            Id: formTarget.Id,
            UserId: userId,
            StartDate: toApiDateTime(StartDate),
            WeekCount,
          })
        : await AddManualUserCoaching(userAdminToken, {
            UserId: userId,
            CoachId,
            StartDate: toApiDateTime(StartDate),
            WeekCount,
          });

      if (isSuccess(res) && res.data.ResponseData === true) {
        toast.success(
          isUpdate ? "Koçluk süresi güncellendi." : "Koç başarıyla atandı."
        );
        closeForm();
        refreshCoachings();
      } else {
        toast.error(
          responseMessage(
            res,
            isUpdate ? "Koçluk süresi güncellenemedi." : "Koç atanamadı."
          )
        );
      }
    } catch (err) {
      console.error("Coaching form submit error", err);
      toast.error("Bir hata oluştu, lütfen tekrar deneyiniz.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (closeReason) => {
    const target = closeTarget;
    setCloseTarget(null);
    if (!target || closeReason == null) return;
    setSubmitting(true);
    try {
      const res = await CloseUserCoaching(userAdminToken, {
        Id: target.Id,
        CloseReason: closeReason,
      });
      if (isSuccess(res) && res.data.ResponseData === true) {
        toast.success(
          "Koçluk kapatıldı. Yeni koç atamak için \"Manuel Koç Ata\"yı kullanabilirsiniz."
        );
      } else {
        toast.error(responseMessage(res, "Koçluk kapatılamadı."));
      }
      // Refetch either way: "zaten kapalı" means our list was stale.
      refreshCoachings();
    } catch (err) {
      console.error("CloseUserCoaching error", err);
      toast.error("Bir hata oluştu, lütfen tekrar deneyiniz.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---- Render ----------------------------------------------------------- */
  return (
    <div className="uc-section">
      <div className="uc-toolbar">
        <div className="uc-user-meta">
          Kullanıcı ID: <strong>{userId}</strong>
          {userLabel && <span> · {userLabel}</span>}
        </div>

        <div className="uc-header-actions">
          {hasOpenCoaching && (
            <span className="uc-hint">
              Yeni koç atamak için önce aktif koçluğu kapatınız.
            </span>
          )}
          <button
            type="button"
            className="uc-btn uc-btn-primary"
            onClick={openAssign}
            disabled={coachingsLoading || hasOpenCoaching || submitting}
          >
            + Manuel Koç Ata
          </button>
        </div>
      </div>

      <div className="uc-table-scroll">
        <table className="uc-table">
          <thead>
            <tr>
              <th>Kayıt ID</th>
              <th>Koç</th>
              <th>Başlangıç</th>
              <th>Bitiş</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {coachingsLoading ? (
              <tr>
                <td colSpan={6} className="uc-table-empty">
                  Koçluk kayıtları yükleniyor…
                </td>
              </tr>
            ) : coachings.length === 0 ? (
              <tr>
                <td colSpan={6} className="uc-table-empty">
                  Bu kullanıcıya ait koçluk kaydı bulunamadı
                </td>
              </tr>
            ) : (
              coachings.map((c) => {
                const state = getCoachingState(c);
                return (
                  <tr key={c.Id}>
                    <td>{c.Id}</td>
                    <td>
                      <div className="uc-coach-cell">
                        <span>{c.CoachName || "-"}</span>
                        {c.CoachEmail && (
                          <span className="uc-muted">{c.CoachEmail}</span>
                        )}
                      </div>
                    </td>
                    <td>{formatDateTime(c.StartDate)}</td>
                    <td>{formatDateTime(c.EndDate)}</td>
                    <td>
                      <span className={`uc-status uc-status-${state}`}>
                        {CoachingStateLabel[state]}
                      </span>
                    </td>
                    <td>
                      {/* No actions on closed or expired records. */}
                      {c.RowStatus && state !== CoachingState.Expired ? (
                        <div className="uc-row-actions">
                          <button
                            type="button"
                            className="uc-btn uc-btn-secondary uc-btn-sm"
                            onClick={() => openUpdate(c)}
                            disabled={submitting}
                          >
                            Süre Güncelle
                          </button>
                          <button
                            type="button"
                            className="uc-btn uc-btn-danger uc-btn-sm"
                            onClick={() => setCloseTarget(c)}
                            disabled={submitting}
                          >
                            Koçluğu Kapat
                          </button>
                        </div>
                      ) : (
                        <span className="uc-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <CoachingFormDialog
        open={formMode !== null}
        mode={formMode}
        coaching={formTarget}
        coachOptions={coachOptions}
        coachesLoading={coachesLoading}
        submitting={submitting}
        onSubmit={handleFormSubmit}
        onClose={closeForm}
      />

      <CloseCoachingDialog
        coaching={closeTarget}
        onConfirm={handleClose}
        onClose={() => setCloseTarget(null)}
      />
    </div>
  );
}
